import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

const NAVY = "#123B5D";
const BORDER = "#CBD8E3";
const MUTED = "#64748B";
const ERROR = "#D32F2F";

export default function PopupInputField({ name, label = "", value, onChange }) {
  const [open, setOpen] = useState(false);
  const [popupValue, setPopupValue] = useState("");
  const [error, setError] = useState(false);

  const textAreaRef = useRef(null);
  const suppressFocusRef = useRef(false);

  useEffect(() => {
    if (open && textAreaRef.current) {
      requestAnimationFrame(() => {
        textAreaRef.current?.focus();

        const length = textAreaRef.current?.value?.length || 0;

        textAreaRef.current?.setSelectionRange(length, length);
      });
    }
  }, [open]);

  const handleInputClick = () => {
    if (suppressFocusRef.current) {
      suppressFocusRef.current = false;
      return;
    }

    setPopupValue(value || "");
    setError(false);
    setOpen(true);
  };

  const handlePopupSubmit = () => {
    if (popupValue.trim() === "") {
      setError(true);

      requestAnimationFrame(() => {
        textAreaRef.current?.focus();
      });

      return;
    }

    setError(false);

    onChange({
      target: {
        name,
        value: popupValue,
      },
    });

    suppressFocusRef.current = true;

    setOpen(false);
  };

  const handleCancel = () => {
    suppressFocusRef.current = true;
    setError(false);
    setOpen(false);
  };

  return (
    <>
      <TextField
        fullWidth
        size="small"
        value={value || ""}
        readOnly
        onClick={handleInputClick}
        onFocus={(e) => {
          if (suppressFocusRef.current) {
            suppressFocusRef.current = false;
            return;
          }

          handleInputClick();
        }}
        placeholder={label}
        sx={{
          "& .MuiOutlinedInput-root": {
            minHeight: 40,
            borderRadius: "7px",
            fontSize: 13.5,
            color: "#243746",
            backgroundColor: "#FFFFFF",
            "& fieldset": {
              borderColor: BORDER,
            },
            "&:hover fieldset": {
              borderColor: "#9FB4C5",
            },
            "&.Mui-focused fieldset": {
              borderColor: NAVY,
              borderWidth: 1,
            },
          },
          "& .MuiInputBase-input": {
            cursor: "text",
          },
          "& .MuiInputBase-input::placeholder": {
            color: "#94A3B8",
            opacity: 1,
            fontSize: 13.5,
          },
        }}
      />

      <Dialog
        open={open}
        onClose={handleCancel}
        fullWidth
        maxWidth="sm"
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: "10px",
            overflow: "hidden",
            border: `1px solid ${BORDER}`,
            boxShadow: "0 20px 50px rgba(15,35,55,0.24)",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2,
            py: 1.5,
            minHeight: 58,
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: NAVY,
            color: "#FFFFFF",
            fontSize: 16,
            fontWeight: 700,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {error && (
              <WarningAmberOutlinedIcon
                sx={{
                  fontSize: 20,
                  color: "#FFCC80",
                }}
              />
            )}

            <span>Enter {name}</span>
          </Box>

          <IconButton
            onClick={handleCancel}
            aria-label="Close"
            sx={{
              width: 34,
              height: 34,
              color: "#FFFFFF",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.10)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            px: 2,
            pt: 2.5,
            pb: 2,
            mt: 2,
            backgroundColor: "#FFFFFF",
          }}
        >
          <TextField
            inputRef={textAreaRef}
            fullWidth
            multiline
            rows={5}
            value={popupValue}
            onChange={(e) => {
              setPopupValue(e.target.value);

              if (error && e.target.value.trim()) {
                setError(false);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handlePopupSubmit();
              }
            }}
            placeholder="Enter your text here..."
            error={error}
            helperText={
              error
                ? "Please enter a value."
                : "Press Enter to save or Shift + Enter for a new line."
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "7px",
                fontSize: 14,
                lineHeight: 1.5,
                alignItems: "flex-start",
                "& fieldset": {
                  borderColor: error ? ERROR : BORDER,
                },
                "&:hover fieldset": {
                  borderColor: error ? ERROR : "#9FB4C5",
                },
                "&.Mui-focused fieldset": {
                  borderColor: error ? ERROR : NAVY,
                  borderWidth: 1.5,
                },
              },
              "& .MuiInputBase-input": {
                padding: "10px 11px",
              },
              "& .MuiFormHelperText-root": {
                marginLeft: 0,
                marginTop: 0.75,
                fontSize: 11.5,
                color: error ? ERROR : MUTED,
              },
            }}
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: 2,
            py: 1.5,
            borderTop: `1px solid ${BORDER}`,
            backgroundColor: "#F8FAFC",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCancel}
            sx={{
              minWidth: 88,
              minHeight: 36,
              borderRadius: "7px",
              textTransform: "none",
              fontSize: 13,
              fontWeight: 700,
              color: NAVY,
              borderColor: "#B9CBD9",
              backgroundColor: "#FFFFFF",
              "&:hover": {
                borderColor: NAVY,
                backgroundColor: "#F1F6FA",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handlePopupSubmit}
            sx={{
              minWidth: 88,
              minHeight: 36,
              borderRadius: "7px",
              textTransform: "none",
              fontSize: 13,
              fontWeight: 700,
              backgroundColor: NAVY,
              "&:hover": {
                backgroundColor: "#0E304B",
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
